import React, { useState, useEffect } from 'react';
import { WeatherIcon } from './WeatherIcon';
import { WeatherData } from '../../types/weather';
import { getDefaultWeather } from '../../services/weather/weatherService';

interface DefaultWeatherWidgetProps {
  location: string;
  className?: string;
  onWeatherLoaded?: (data: WeatherData) => void;
}

export const DefaultWeatherWidget: React.FC<DefaultWeatherWidgetProps> = ({ 
  location,
  className = '',
  onWeatherLoaded
}) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [shimmerVisible, setShimmerVisible] = useState(true);

  useEffect(() => {
    let mounted = true;
    let shimmerTimer: NodeJS.Timeout;
    let contentTimer: NodeJS.Timeout;

    async function fetchWeather() {
      if (!location) return;
      
      try {
        setIsLoading(true);
        setShimmerVisible(true);
        setShowContent(false);
        setError(null);
        
        const data = await getDefaultWeather(location);
        
        if (mounted) {
          setWeather(data);
          
          // First hide shimmer
          shimmerTimer = setTimeout(() => {
            setShimmerVisible(false);
            
            // Then show content after shimmer fades out
            contentTimer = setTimeout(() => {
              setShowContent(true);
              setIsLoading(false);
            }, 300); // Slight delay after shimmer disappears
            
          }, 500); // Allow shimmer to show for a minimum time
          
          if (typeof onWeatherLoaded === 'function') {
            onWeatherLoaded(data);
          }
        }
      } catch (err) {
        if (mounted) {
          setError('Unable to load weather data');
          setShimmerVisible(false);
          setIsLoading(false);
        }
      }
    }

    fetchWeather();
    
    return () => { 
      mounted = false; 
      clearTimeout(shimmerTimer);
      clearTimeout(contentTimer);
    };
  }, [location]);

  return (
    <div className={`w-full bg-white rounded-lg ${className} relative`} 
         style={{ padding: 'var(--weather-widget-padding)', minHeight: '150px' }}>
      
      {/* Shimmer loader */}
      {shimmerVisible && (
        <div className="animate-pulse space-y-4 absolute top-0 left-0 right-0 transition-opacity duration-500"
             style={{ 
               opacity: shimmerVisible ? 1 : 0,
               padding: 'var(--weather-widget-padding)'
             }}>
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="h-5 bg-gray-200 rounded w-28"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-7 bg-gray-200 rounded w-14"></div>
              <div className="h-7 bg-gray-200 rounded-full w-7"></div>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mt-3">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="space-y-2 flex flex-col items-center">
                <div className="h-2 bg-gray-200 rounded w-8"></div>
                <div className="h-5 bg-gray-200 rounded-full w-5 mx-auto"></div>
                <div className="h-2 bg-gray-200 rounded w-6"></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actual weather data */}
      {weather && !error && (
        <div className="transition-opacity duration-500 ease-in-out"
             style={{ 
               opacity: showContent ? 1 : 0,
               visibility: showContent ? 'visible' : 'hidden'
             }}>
          {/* Weather content */}
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-medium text-gray-800" style={{ fontSize: 'var(--weather-title-size)' }}>{location.charAt(0).toUpperCase() + location.slice(1)}</h3>
            <div className="flex items-center">
              <span className="font-medium text-gray-900" style={{ fontSize: 'var(--weather-temp-size)' }}>
                {Math.round(weather.temperature)}°
              </span>
              <WeatherIcon 
                condition={weather.condition} 
                size="medium"
                className="ml-1" 
                style={{ width: 'var(--weather-icon-size)', height: 'var(--weather-icon-size)' }}
                animated
              />
            </div>
          </div>

          {/* Weather forecast */}
          <div className="w-full">
            <h4 className="font-medium text-gray-700 mb-2" style={{ fontSize: 'var(--weather-forecast-title-size)' }}>
              7-Day Forecast
            </h4>
            
            <div className="grid grid-cols-7 gap-1">
              {weather.forecast.map((day, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="font-medium text-gray-800 truncate w-full" style={{ fontSize: 'var(--weather-day-size)' }}>
                    {day.time}
                  </div>
                  <div className="text-gray-400" style={{ fontSize: 'var(--weather-date-size)' }}>
                    {new Date(day.date).getDate()}
                  </div>
                  <WeatherIcon 
                    condition={day.condition} 
                    size="small"
                    className="my-1" 
                    style={{ width: 'var(--weather-forecast-icon-size)', height: 'var(--weather-forecast-icon-size)' }}
                  />
                  <div className="font-medium text-gray-900" style={{ fontSize: 'var(--weather-forecast-temp-size)' }}>
                    {Math.round(day.temperature)}°
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="text-sm text-gray-500 transition-opacity duration-300 ease-in"
             style={{ opacity: showContent ? 1 : 0 }}>
          {error}
        </div>
      )}
    </div>
  );
};